import { ChangeDetectionStrategy, Component, model, OnDestroy, signal } from '@angular/core';

import { TableCellDescriptor } from '../../../../model.module/table-cell.model';
import { SortType } from '../../../../model.module/constant/sort-types.enum';
import { AppState } from '../../../../app.state';
import { PipeFunctions } from '../../../../pipes.module/_pipe.functions';
import { FilterFunctions } from '../../../../pipes.module/_filter.functions';
import { AuthService } from '../../../../services.module/auth.service';
import { Router } from '@angular/router';
import { Permission } from '../../../../model.module/constant/rights.enum';

import { BuergschaftsAntragTypes } from '../../../../model.module/bgsch-antrag-type.model';
import { BuergscheinLight, VertragData } from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import { VertraegeService } from '../../../../providers.module/vertraege.service';
import { BuergscheineService } from '../../../../providers.module/buergscheine.service';
import { ObligoApiService } from '../../../../../generated/api/obligo/obligo.service';
import { SortFunctions } from '../../../../pipes.module/_sort.functions';

@Component({
    selector: 'list-buergschaft',
    templateUrl: './bgsch-list.component.html',
    styleUrls: ['../../../commonless/content.less', '../../../commonless/buergschaften.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuergschaftenListComponent implements OnDestroy {

    static readonly buergscheinTargetPathStub: string = '/kreditportal/buergschaften';

    static readonly createBuergscheinPath: string = '/kreditportal/buergschaften/create-buergschein';

    static readonly obligoPath: string = '/kreditportal/buergschaften/obligo';

    static readonly buergschaftsstatusMapping = {
        "1": "Ausgestellt",
        "2": "Storno",
        "3": "Zurückgegeben",
        "4": "Teilenthaftet",
        "5": "Inanspruchnahme",
        "6": "Befristung ausgelaufen",
        "7": "Aktiv",
        "99": "Sonstiger"
    }

    static readonly statusanzeigeBuergschaftMapping = {
        "IN_BEARBEITUNG": "In Bearbeitung",
        "AUSGESTELLT": "Ausgestellt"
    }

    static readonly filterCellNames: string[] = [
        'buergscheinId',
        'fassungsBetrag',
        'auftraggeberInfo',
        'ausstellungsDatum',
        'auftragInfo1'
    ];

    readonly emptyVertrag = '';
    readonly emptyGeschaeftsjahr = '';

    currentOffset = signal(0);

    selectedVsnr = model<string>(this.emptyVertrag);

    selectedGeschaeftsjahr = model<string>(this.emptyGeschaeftsjahr);

    vertraegeList = signal<VertragData[]>([]);

    geschaeftsjahreList = signal<number[]>([]);

    visibleBuergscheine = signal<BuergscheinLight[]>(null);

    currBuergscheine: BuergscheinLight[];

    public static readonly hoverTextFunction = (displayText: string): string => {
        return BuergschaftsAntragTypes.findByBuergschaftsart(displayText).label;
    };

    public static readonly buergscheineMetaDataTemplate = [
        new TableCellDescriptor(
            'Bürgscheinnummer',
            'buergscheinId',
            null,
            true,
            SortType.BUERGSCHEIN_NUMBER,
            '20%',
            true,
        ),
        new TableCellDescriptor(
            'Bürgschaftsart',
            'fassungsArt',
            'hover_fassungsart',
            true,
            SortType.ALPHABETICAL,
            '15%',
            false,
            BuergschaftenListComponent.hoverTextFunction, // ODOT kann wech
        ),
        new TableCellDescriptor(
            'Höhe der Bürgschaft',
            'fassungsBetrag',
            'formatMoney',
            true,
            SortType.NUMERICAL,
            '15%',
        ),
        new TableCellDescriptor(
            'Auftraggeber',
            'auftraggeberInfo',
            null,
            true,
            SortType.ALPHABETICAL,
            '30%'
        ),
        new TableCellDescriptor(
            'Status',
            'buergschaftsStatus',
            null,
            true,
            SortType.ALPHABETICAL,
            '20%'
        ),
    ];

    buergscheinMetaData: TableCellDescriptor[] = BuergschaftenListComponent.buergscheineMetaDataTemplate;

    constructor(
        private readonly state: AppState,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly vertraegeService: VertraegeService,
        private readonly buergscheineService: BuergscheineService,
        private readonly obligoApiService: ObligoApiService
    ) {
        const preSelectedVsnr = this.state.lastVertragDisplayed
            ? this.state.lastVertragDisplayed.vsnrNumerisch
            : this.emptyVertrag;

        this.selectedVsnr.set(preSelectedVsnr);

        this.loadVertraege();

        this.authService.addOnKundenUpdateFn(() => this.loadVertraege);
    }

    private loadVertraege() {
        this.vertraegeService.getVertrage().subscribe(vertraegeListe => {
            const anzuzeigendeVertrage = vertraegeListe.filter(v => !v.fehlerText);

            this.vertraegeList.set(anzuzeigendeVertrage);

            if (this.state.lastVertragDisplayed) {
                // nur Jahre aktualisieren, weil nur vsnr vorbelegt ist
                this.notifyChangedVsnr();
            } else {
                this.refreshList();
                if (anzuzeigendeVertrage.length === 1) {
                    this.selectedVsnr.set(anzuzeigendeVertrage[0].vertragHandle.vsnrNumerisch);
                    this.notifyChangedVsnr();
                }
            }

            this.state.storeURL();
            if (anzuzeigendeVertrage && anzuzeigendeVertrage.length === 1) {
                this.selectedVsnr.set(anzuzeigendeVertrage[0].vertragHandle.vsnrNumerisch);
                this.refreshList();
            }
        });
    }

    canAddBs(): boolean {
        return (this.authService.getRole() & Permission.READ_CREATE_BS) === Permission.READ_CREATE_BS;
    }

    ngOnDestroy(): void {
        this.selectedGeschaeftsjahr.set(this.emptyGeschaeftsjahr);
        this.selectedVsnr.set(this.emptyVertrag);

        if (this.currBuergscheine) {
            this.currBuergscheine = [];
        }

        if (this.visibleBuergscheine()) {
            this.visibleBuergscheine.set([]);
        }
    }

    notifyChangedVsnr(): void {
        this.visibleBuergscheine.set([]);

        if (!this.selectedVsnr()) {
            return;
        }

        this.obligoApiService.getObligoJahre(this.selectedVsnr()).subscribe({
            next: obligoJahreResponse => {
                const obligoJahre = obligoJahreResponse.jahre;

                this.geschaeftsjahreList.set(obligoJahre);

                // Seite wurde wieder aufgerufen und es wurde in der Vergangenheit ein Jahr ausgewaehlt
                if (   this.state.getLastGeschaeftsjahrDisplayed()
                    && obligoJahre.indexOf(+this.state.getLastGeschaeftsjahrDisplayed()) >= 0
                ) {
                    this.selectedGeschaeftsjahr.set(this.state.getLastGeschaeftsjahrDisplayed());
                }

                if (obligoJahre.length === 1) {
                    this.selectedGeschaeftsjahr.set(obligoJahre[0] + '');
                }

                this.refreshList();
            },
            error: () => {
                // keine Buergscheine vorhanden
                this.selectedGeschaeftsjahr.set(this.emptyGeschaeftsjahr);
                this.visibleBuergscheine.set([]);
                this.geschaeftsjahreList.set([]);
                this.refreshList();
            }
        });
    }

    notifyChangedYear(): void {
        this.refreshList();
    }

    private refreshList(): void {
        if (this.selectedGeschaeftsjahr() === this.emptyGeschaeftsjahr || this.selectedVsnr() === this.emptyVertrag) {
            return;
        }

        this.state.setLastGeschaeftsjahrDisplayed(this.selectedGeschaeftsjahr());

        this.vertraegeService.getVertrag(this.selectedVsnr()).subscribe(vertragData => {
            this.state.lastVertragDisplayed = vertragData.vertragHandle;

            const vsnr = vertragData.vertragHandle.vsnrNumerisch;
            const geschaeftsjahr = parseInt(this.selectedGeschaeftsjahr(), 10);

            this.buergscheineService.getBuergscheine(vsnr, geschaeftsjahr).subscribe(buergscheinListe => {
                if (!buergscheinListe || !buergscheinListe.buergscheine) {
                    this.currBuergscheine = [];
                    this.visibleBuergscheine.set([]);
                    return;
                }

                this.currBuergscheine = buergscheinListe.buergscheine;

                const trimmedAndSortedBuergscheine = BuergschaftenListComponent.trimBuergscheine(
                    buergscheinListe.buergscheine
                );

                SortFunctions.sortArray4Buergscheine(trimmedAndSortedBuergscheine, 'buergscheinId', true);

                this.visibleBuergscheine.set(trimmedAndSortedBuergscheine);
            });
        });
    }

    searchBuergschein = (term: string) => {
        const temp = FilterFunctions.filterMatrix(
            this.currBuergscheine,
            BuergschaftenListComponent.filterCellNames,
            term
        );

        this.visibleBuergscheine.set(BuergschaftenListComponent.trimBuergscheine(temp));
    };

    goToBuergscheinAction = (buergscheinNummer: string): void => {
        this.router.navigate(
            [BuergschaftenListComponent.buergscheinTargetPathStub, this.selectedVsnr()],
            { queryParams: { param: buergscheinNummer } },
        );
    };

    goToAddBuergscheinAction = (): void => {
        this.router.navigate([BuergschaftenListComponent.createBuergscheinPath]);
    };

    goToObligo = (): void => {
        this.router.navigate([BuergschaftenListComponent.obligoPath]);
    };

    pagerAction = (page: number) => {
        this.currentOffset.set(page);
    };

    static trimBuergscheine(input: BuergscheinLight[]): BuergscheinLight[] {
        const result: BuergscheinLight[] = [];

        for (const b of input) {
            const copy: BuergscheinLight = structuredClone(b);

            if (b.statusanzeigeBuergschaft in BuergschaftenListComponent.statusanzeigeBuergschaftMapping) {
                copy.buergschaftsStatus = BuergschaftenListComponent.statusanzeigeBuergschaftMapping[b.statusanzeigeBuergschaft];
            } else if (b.buergschaftsStatus in BuergschaftenListComponent.buergschaftsstatusMapping) {
                // Für digitale BS gibt es das hier
                copy.buergschaftsStatus = BuergschaftenListComponent.buergschaftsstatusMapping[b.buergschaftsStatus];
            } else {
                copy.buergschaftsStatus = '';
            }

            if (!b.fassungsArt || b.fassungsArt === 'UNKNOWN') {
                // @ts-ignore
                copy.fassungsArt = 'SO' // Sonstige Bürgschaft
            }

            copy.ausstellungsDatum = PipeFunctions.pickYearOfDeDate(b.ausstellungsDatum);
            copy.auftraggeberInfo = PipeFunctions.pickCustomer(b.auftraggeberInfo);

            result.push(copy);
        }

        return result;
    }

}
